import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LuFileSpreadsheet, LuUserCheck, LuUserPlus, LuUserX, LuUsers } from 'react-icons/lu';
import DataCardList from '../../components/data/DataCardList';
import DataTable from '../../components/data/DataTable';
import DataToolbar from '../../components/data/DataToolbar';
import FilterSheet from '../../components/data/FilterSheet';
import PageHero from '../../components/data/PageHero';
import StatTiles from '../../components/data/StatTiles';
import TablePagination from '../../components/data/TablePagination';
import Button from '../../components/ui/Button';
import useElementWidth from '../../hooks/useElementWidth';
import useMediaQuery from '../../hooks/useMediaQuery';
import usePagedList from '../../hooks/usePagedList';
import useTableColumns from '../../hooks/useTableColumns';
import useViewportFit from '../../hooks/useViewportFit';
import ExternalPersonnelFormModal from './ExternalPersonnelFormModal';
import useExternalPersonnel from './useExternalPersonnel';
import useExternalPersonnelText from './externalPersonnelText';
import {
  COLUMN_KEYS,
  DEFAULT_VISIBLE_COLUMNS,
  MIN_CHARS,
  MIN_COLUMN_WIDTH,
  PERSONNEL_COLUMNS,
  PINNED_COLUMNS,
  STATUS_VALUES,
  getFilterState,
} from './externalPersonnelColumns';
import PersonnelCell, { PersonnelCardRow } from './parts/PersonnelCell';
import PersonnelDetailPane from './parts/PersonnelDetailPane';

const COLUMNS_STORAGE_KEY = 'externalPersonnel.tableColumns.v1';
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 400;
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

/**
 * Harici Personel Yönetimi (Tailwind), built like the other data pages:
 * tiles that filter, a table card that reaches at most the bottom of the
 * screen (Aktif / İnaktif / Tümü switch, header filters, column menu), the
 * selected person's panel and one dialog for adding and editing.
 */
const ExternalPersonnelManagementPage = () => {
  const {
    personnels, isLoading, filteredPersonnels, stats, filters, handleFilter, clearFilters,
    searchTerm, setSearchTerm, selectedPersonnel, setSelectedId, downloadExcel, refresh,
  } = useExternalPersonnel();
  const { language, locale, label, fieldLabel, statusLabel } = useExternalPersonnelText();

  const columns = useTableColumns({
    storageKey: COLUMNS_STORAGE_KEY,
    columns: PERSONNEL_COLUMNS,
    defaultVisible: DEFAULT_VISIBLE_COLUMNS,
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  // Sayfa başına sabit 10 satır (kullanıcı isteği): kart ekrana sığdırılmaz, tablo satır sayısını ölçmez
  const maxCardHeight = undefined;
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [form, setForm] = useState({ open: false, personnel: null, key: 0 });
  const hasLoaded = personnels.length > 0 || !isLoading;

  const resetKey = JSON.stringify([filters, searchTerm]);
  const { pageRows, pageStart, currentPage, totalPages, pageSize, setPageSize, goToPage, reveal } = usePagedList(filteredPersonnels, { resetKey });

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  useEffect(() => {
    if (!selectedPersonnel) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || showFilterSheet) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedId(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedPersonnel, showFilterSheet, setSelectedId]);

  const statusOptions = useMemo(() => STATUS_VALUES.map((value) => ({ value, label: statusLabel(value) })), [statusLabel]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: fieldLabel(key),
    filter: { type: PERSONNEL_COLUMNS[key].type, options: key === 'Durum' ? statusOptions : undefined },
  })), [columns.visibleKeys, fieldLabel, statusOptions]);

  // Durum seçimi araç çubuğundaki düğmede görünür; çip olarak tekrarlanmaz
  const filterChips = Object.keys(filters)
    .filter((key) => key !== 'Durum' && PERSONNEL_COLUMNS[key] && getFilterState(key, filters[key]) !== 'off')
    .map((key) => ({ key, label: fieldLabel(key), value: String(filters[key]), state: getFilterState(key, filters[key]) }));

  const statusValue = filters['Durum'] || '';
  const searchActive = searchTerm.trim().length >= MIN_CHARS;
  const isFilterActive = statusValue !== 'aktif' || searchActive || Object.entries(filters).some(([key, value]) => key !== 'Durum' && getFilterState(key, value) === 'applied');

  const handlePageJump = (text) => {
    const target = parseInt(text, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(label('toast.pageJumpInvalid', { max: totalPages }));
      return false;
    }
    goToPage(target);
    return true;
  };

  const selectedIndex = selectedPersonnel ? filteredPersonnels.findIndex((p) => p.id === selectedPersonnel.id) : -1;
  const selectSibling = (offset) => {
    const sibling = filteredPersonnels[selectedIndex + offset];
    if (!sibling) return;
    setSelectedId(sibling.id);
    reveal(sibling.id);
  };

  const openForm = (personnel = null) => setForm((current) => ({ open: true, personnel, key: current.key + 1 }));
  const closeForm = () => setForm((current) => ({ ...current, open: false }));
  const setStatus = useCallback((value) => handleFilter('Durum', value), [handleFilter]);

  const shareOf = (count) => (stats.total > 0 ? Math.min(1, count / stats.total) : 0);
  const shareTitle = (count) => label('stats.share', { percent: Math.round(shareOf(count) * 100) });
  const tiles = [
    { key: 'total', label: label('stats.total'), shortLabel: label('stats.totalShort'), value: stats.total, icon: LuUsers, gradient: 'tw-from-fuchsia-400 tw-to-purple-600 tw-shadow-fuchsia-500/30', onClick: () => { clearFilters(); }, active: !statusValue && !searchActive },
    { key: 'active', label: label('stats.active'), shortLabel: label('stats.activeShort'), value: stats.active, icon: LuUserCheck, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30', share: shareOf(stats.active), shareTitle: shareTitle(stats.active), barClass: 'tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500', onClick: () => setStatus('aktif'), active: statusValue === 'aktif' },
    { key: 'inactive', label: label('stats.inactive'), shortLabel: label('stats.inactiveShort'), value: stats.inactive, icon: LuUserX, gradient: 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30', onClick: () => setStatus('inaktif'), active: statusValue === 'inaktif' },
  ];

  const paneProps = {
    personnel: selectedPersonnel,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: filteredPersonnels.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < filteredPersonnels.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedId(null),
    onEdit: () => openForm(selectedPersonnel),
  };

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <PageHero
          icon={LuUsers}
          gradient="tw-from-fuchsia-400 tw-to-purple-600 tw-shadow-fuchsia-500/30"
          title={label('page.title')}
          subtitle={label('page.subtitle')}
          actionsClassName="tw-grid tw-grid-cols-[auto_minmax(0,1fr)] tw-items-center tw-gap-2 sm:tw-flex sm:tw-flex-wrap lg:tw-justify-end"
        >
          <Button onClick={() => downloadExcel(columns.visibleKeys)} aria-label={label('actions.excel')} title={label('actions.excelHint')} className="max-sm:tw-w-10 max-sm:tw-px-0">
            <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
            <span className="tw-hidden sm:tw-inline">{label('actions.excel')}</span>
          </Button>
          <Button variant="primary" onClick={() => openForm(null)} className="max-sm:tw-w-full">
            <LuUserPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('actions.add')}
          </Button>
        </PageHero>

        <StatTiles tiles={tiles} ready={hasLoaded} />

        <div ref={tableAreaRef} className="tw-flex tw-items-start tw-gap-4">
          <section ref={listCardRef} aria-label={label('table.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
            <DataToolbar
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: label('toolbar.search'),
                placeholderShort: label('toolbar.searchShort'),
                label: label('toolbar.search'),
                minLength: MIN_CHARS,
              }}
              status={{
                options: ['aktif', 'inaktif', ''].map((value) => ({ value, label: value ? statusLabel(value) : label('toolbar.all') })),
                value: statusValue,
                onChange: setStatus,
                layoutId: 'external-personnel-status',
                label: label('toolbar.status'),
              }}
              columnMenu={{
                columns: COLUMN_KEYS.map((key) => ({ key, label: fieldLabel(key) })),
                visibleColumns: columns.visibleColumns,
                onToggle: columns.toggleColumn,
                onShowAll: columns.showAllColumns,
                onReset: columns.resetColumns,
              }}
              compact={compactList}
              onOpenFilters={() => setShowFilterSheet(true)}
              chips={filterChips}
              onRemoveChip={(key) => handleFilter(key, '')}
              onClearAll={clearFilters}
            />

            {compactList ? (
              <DataCardList
                label={label('table.listLabel')}
                rows={pageRows}
                renderCard={(personnel) => <PersonnelCardRow personnel={personnel} />}
                selectedId={selectedPersonnel?.id}
                onSelect={(personnel) => setSelectedId(personnel.id)}
                isLoading={isLoading}
                isBusy={false}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
              />
            ) : (
              <DataTable
                label={label('table.listLabel')}
                rows={pageRows}
                columns={tableColumns}
                getColumnWidth={columns.getColumnWidth}
                onResizeColumn={columns.setColumnWidth}
                onResetColumnWidth={columns.resetColumnWidth}
                pinnedKeys={PINNED_COLUMNS}
                filters={filters}
                onFilterChange={handleFilter}
                getFilterState={getFilterState}
                renderCell={(personnel, key) => <PersonnelCell columnKey={key} personnel={personnel} />}
                getCellTitle={(personnel, key) => (personnel[key] ? String(personnel[key]) : undefined)}
                selectedId={selectedPersonnel?.id}
                onSelect={(personnel) => setSelectedId(personnel.id)}
                isLoading={isLoading}
                isBusy={false}
                skeletonRows={pageSize}
                maxCardHeight={maxCardHeight}
                onCapacityChange={setPageSize}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
                onShowColumns={columns.resetColumns}
              />
            )}

            <TablePagination
              rangeStart={pageStart + 1}
              rangeEnd={pageStart + pageRows.length}
              resultCount={filteredPersonnels.length}
              rangeOf={label('table.rangeOf', { total: filteredPersonnels.length.toLocaleString(locale) })}
              ofTotal={filteredPersonnels.length !== stats.total ? label('table.ofTotal', { total: stats.total.toLocaleString(locale) }) : undefined}
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={false}
              compact={compactList}
              onPage={goToPage}
              jump={{ label: label('table.jump'), onJump: handlePageJump }}
            />
          </section>

          {dockPanel && <PersonnelDetailPane docked height={fitHeight} {...paneProps} />}
        </div>
      </div>

      {!dockPanel && <PersonnelDetailPane {...paneProps} />}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={COLUMN_KEYS.map((key) => ({ key, label: fieldLabel(key), type: PERSONNEL_COLUMNS[key].type, options: key === 'Durum' ? statusOptions : undefined }))}
        filters={filters}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearFilters}
        resultCount={filteredPersonnels.length}
      />

      <ExternalPersonnelFormModal
        key={form.key}
        isOpen={form.open}
        personnel={form.personnel}
        onClose={closeForm}
        onSaved={refresh}
      />
    </>
  );
};

export default ExternalPersonnelManagementPage;
